import { Kaggle } from "@kaggle/sdk";
import { getServices } from "../../../../api/getServices";
import {
  getFirestoreClient,
  stopListeners,
} from "../../../redux/actions/firebase";
import { showForkSecretModal } from "../../../redux/actions/modal";
import { EditorStatus } from "../../../types/EditorStatus";
import { normalizeAmbiguousErrorMessages } from "../../../utils/ambiguousErrors";
import { appendTerminalError } from "../../actions/terminal";
import {
  editorErrorOccurred,
  setEditorStatus,
  userErrorOccurred,
} from "../editor";
import { handleQuotaError, isQuotaError } from "../loadKernel";
import { setRun } from "../notebook";
import { fetchRunSuccess } from "../runs";
import { getDockerImages } from "./docker";

export const requestWorker = async (
  params: Kaggle.Sdk.Kernels.CreateKernelSessionRequest
) => {
  const services = getServices();
  try {
    // We need to stopListeners first so that existing Firestore listeners won't change
    // the editor status due to events on the old session.
    // Then we need to (without yeilding flow, ie. no await) set to STARTING state so that
    // requestWorker can't race with other requestWorker calls (causing a double start).
    stopListeners();
    setEditorStatus(EditorStatus.STARTING);
    const session =
      await services.kernelsClient.kernelsService.createKernelSession(params);

    if (!session) {
      throw new Error("Failed to start kernel.");
    }

    if (session.error) {
      throw new Error(session.error);
    }

    if (!session.id) {
      throw new Error("Worker created does not have a kernel run id.");
    }

    setRun(session.id);
    fetchRunSuccess(session.id);
    const firestoreClient = await getFirestoreClient();
    await firestoreClient.resetAllFirestoreListeners(session.id);
    // We don't know the kernelIdPath here, but the resources should always be
    // non-null, so it's ok.
    // Load Docker images used in the Docker environment selector modal.
    getDockerImages(session.id);

    if (session.showParentForkSecretWarning) {
      showForkSecretModal();
    }
    return session;
  } catch (error) {
    const message = normalizeAmbiguousErrorMessages(error).join(", ");
    if (isQuotaError(message)) {
      if (handleQuotaError(message)) {
        // Handled successfully, nothing to do.
      } else {
        userErrorOccurred(message);
      }
      setEditorStatus(EditorStatus.STOPPED);
    } else {
      appendTerminalError("An error occurred requesting the worker.", error);
      editorErrorOccurred(message);
      setEditorStatus(EditorStatus.FATAL);
    }

    throw error;
  }
};
